import React from 'react';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const clinicName = "South Vancouver Medical Clinic";
  const websiteUrl = "DriverMedicalExam.com";
  const serviceEmail = "info@southvanmed.com";
  const address = "350 Southeast Marine Drive, Vancouver, BC V5X 2S5";
  const themeColor = "#0ea5e9"; // sky-500

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b-2" style={{ borderColor: themeColor }}>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">{websiteUrl}</div>
              <h2 className="text-2xl font-semibold text-gray-900">PRIVACY POLICY</h2>
              <div className="text-sm text-gray-500 mt-2">Version 3.0 | Last Updated: January 19, 2026</div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[60vh] overflow-y-auto text-gray-700 leading-relaxed">
            <p className="mb-6">
              <strong>{clinicName}</strong> ("{clinicName}", "we", "us", or "our") owns and operates the website <strong>{websiteUrl}</strong> 
              (the "Website"), including any subdomains or related sites.
            </p>

            <p className="mb-6">
              This Privacy Policy describes how we collect, use, disclose, and protect personal information when you use our Website or services. 
              By accessing or using our Website, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>1. Information We Collect</h3>
            <p className="mb-4">We collect personal information that you voluntarily provide, including but not limited to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Name, date of birth, gender</li>
              <li>Contact information (email, phone number, address)</li>
              <li>Health information (medical history, prescriptions, test results, health card numbers)</li>
              <li>Payment information (credit card details, billing address)</li>
              <li>Account credentials (username, password)</li>
              <li>Appointment and scheduling information</li>
              <li>Communications with our healthcare providers and staff</li>
            </ul>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>2. How We Use Your Information</h3>
            <p className="mb-4">We use your personal information to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Provide medical and healthcare services</li>
              <li>Schedule appointments and send reminders</li>
              <li>Process payments and billing</li>
              <li>Communicate with you regarding your health and treatment</li>
              <li>Maintain medical records as required by law</li>
              <li>Improve our services and Website functionality</li>
              <li>Comply with legal and regulatory obligations</li>
            </ul>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>3. Virtual Care Consent</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
              <p className="mb-4">
                By using our telehealth or virtual care services, you acknowledge and consent to the following:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Virtual care consultations may be conducted via video, phone, or secure messaging</li>
                <li>While we use secure platforms, no electronic communication is completely secure</li>
                <li>Virtual care may have limitations compared to in-person examinations</li>
                <li>You are responsible for providing accurate health information during virtual consultations</li>
                <li>Emergency situations require immediate in-person care or calling 911</li>
                <li>Virtual care sessions may be recorded for quality assurance and medical record purposes (with your consent)</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>4. Information Sharing and Disclosure</h3>
            <p className="mb-4">We may share your personal information with:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Healthcare Providers:</strong> Doctors, specialists, laboratories, and pharmacies involved in your care</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist with payment processing, appointment scheduling, and IT services</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government authorities</li>
              <li><strong>Emergency Situations:</strong> To protect your vital interests or public safety</li>
            </ul>
            <p className="mb-6">We will not sell or rent your personal information to third parties for marketing purposes.</p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>5. Data Security</h3>
            <p className="mb-6">
              We implement reasonable physical, technical, and administrative safeguards to protect your personal information from unauthorized 
              access, disclosure, alteration, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>6. Your Rights</h3>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Access and review your personal information</li>
              <li>Request corrections to inaccurate or incomplete information</li>
              <li>Request deletion of your personal information (subject to legal requirements)</li>
              <li>Withdraw consent for certain uses of your information</li>
              <li>Lodge a complaint with the Privacy Commissioner if you believe your privacy rights have been violated</li>
            </ul>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>7. Cookies and Tracking Technologies</h3>
            <p className="mb-6">
              Our Website uses cookies and similar technologies to enhance user experience, analyze website traffic, and remember your preferences. 
              You can control cookie settings through your browser, but disabling cookies may affect Website functionality.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>8. Third-Party Links</h3>
            <p className="mb-6">
              Our Website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. 
              We encourage you to review their privacy policies before providing any personal information.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>9. Children's Privacy</h3>
            <p className="mb-6">
              Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal information from children 
              without parental consent. Medical services for minors require guardian consent and involvement.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>10. Changes to This Privacy Policy</h3>
            <p className="mb-6">
              We may update this Privacy Policy from time to time. The "Last Updated" date at the top indicates when the policy was last revised. 
              Continued use of our Website after changes constitutes acceptance of the updated policy.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>11. Contact Us</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <p className="mb-3">If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:</p>
              <p className="font-semibold mb-2">{clinicName}</p>
              <p className="mb-1">{address}</p>
              <p className="mb-1">Email: <a href={`mailto:${serviceEmail}`} className="text-sky-600 hover:underline">{serviceEmail}</a></p>
              <p>Phone: <a href="tel:604-323-0077" className="text-sky-600 hover:underline">604-323-0077</a></p>
            </div>

            <p className="mt-8 text-sm text-gray-600 italic">
              This Privacy Policy does not create rights beyond those provided under applicable privacy laws. In case of inconsistency, 
              applicable laws will prevail.
            </p>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-center">
            <button
              onClick={onClose}
              className="px-8 py-2 rounded-md text-white font-medium transition-colors duration-200 hover:opacity-90"
              style={{ backgroundColor: themeColor }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PrivacyPolicyModal };
